import { Button, buttonVariants } from "@patient-watch/ui/button";
import { selectTriggerVariants } from "@patient-watch/ui/select";

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@patient-watch/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@patient-watch/ui/dialog";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@patient-watch/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@patient-watch/ui/popover";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { Check, ChevronsUpDown, Lock, Plus, X } from "lucide-react";
import React from "react";
import { useFormContext } from "react-hook-form";
import { cn } from "@patient-watch/ui";
import type { OptionT } from "./Select";
import { RichLabel } from "./components/RichLabel";
import { getOptionTestId, getTestId } from "./utils/getTestId";
import type { LooseRHFField } from "./utils/loose-rhf-field";
import { reactNodeToPlainString } from "./utils/reactNodeToPlainString";

interface Props {
    label?: string;
    labelClassName?: string;
    name: string;
    description?: React.ReactNode;
    options: OptionT[];
    disabled?: boolean;
    autoFocus?: boolean;
    clearable?: boolean;
    addOption?: React.ReactNode | ((close: () => void) => React.ReactNode);
    /** Sticky row at the bottom of the list, e.g. a link to manage team contacts. */
    footer?: React.ReactNode;
    placeholder?: string;
    /** When set and non-empty, matching options (by `value` as user id) are listed first, then alphabetically within each group. */
    favouriteUserIds?: ReadonlySet<string>;
    /** Renders at the start of each option row (e.g. favourite toggle). Clicks should not select the option. */
    optionTrailing?: (option: OptionT) => React.ReactNode;
    /** App locale tag, e.g. "de-DE". Defaults to English copy. */
    locale?: string;
}

/** ponytail: inline copy - packages/forms has no i18n dependency, callers pass their locale. */
function comboBoxCopy(locale?: string) {
    if (locale === "de-DE") {
        return {
            search: "Suchen…",
            empty: "Keine Optionen gefunden.",
            addOption: "Option hinzufügen",
            clear: "Löschen",
            select: "Auswählen",
        };
    }
    if (locale === "da-DK") {
        return {
            search: "Søg…",
            empty: "Ingen valgmuligheder fundet.",
            addOption: "Tilføj valgmulighed",
            clear: "Ryd",
            select: "Vælg",
        };
    }
    return {
        search: "Search...",
        empty: "No options found.",
        addOption: "Add an Option",
        clear: "Clear",
        select: "Select",
    };
}

function ComboBox({
    options,
    placeholder,
    disabled,
    label,
    labelClassName,
    name,
    description,
    clearable,
    addOption,
    footer,
    favouriteUserIds,
    optionTrailing,
    locale,
}: Props) {
    const form = useFormContext();
    const copy = comboBoxCopy(locale);
    const [open, setOpen] = React.useState(false);
    const [addOptionOpen, setAddOptionOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");

    const displayedOptions = React.useMemo(() => {
        const query = search.toLowerCase();
        const filtered = (options ?? []).filter((o) => {
            if (!query) return true;
            const label = reactNodeToPlainString(o.label).toLowerCase();
            const value = String(o.value ?? "").toLowerCase();
            const searchText = (o.searchText ?? "").toLowerCase();
            return label.includes(query) || value.includes(query) || searchText.includes(query);
        });
        if (!favouriteUserIds || favouriteUserIds.size === 0) {
            return filtered;
        }
        const sortByLabel = (a: OptionT, b: OptionT) =>
            reactNodeToPlainString(a.label).localeCompare(reactNodeToPlainString(b.label), undefined, {
                sensitivity: "base",
            });
        const fav = filtered.filter((o) => favouriteUserIds.has(String(o.value)));
        const rest = filtered.filter((o) => !favouriteUserIds.has(String(o.value)));
        return [...[...fav].sort(sortByLabel), ...[...rest].sort(sortByLabel)];
    }, [options, search, favouriteUserIds]);

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }: { field: LooseRHFField }) => (
                <FormItem className="w-full">
                    {label && (
                        <FormLabel className={labelClassName}>
                            <RichLabel>{label}</RichLabel>
                        </FormLabel>
                    )}
                    {description && <FormDescription>{description}</FormDescription>}
                    <Popover open={open} onOpenChange={setOpen}>
                        <div className="flex items-center">
                            <PopoverTrigger data-testid={getTestId(name, "trigger")} disabled={disabled} asChild>
                                <FormControl>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        role="combobox"
                                        aria-controls={name}
                                        aria-expanded={open}
                                        className={cn(
                                            selectTriggerVariants({ size: "md" }),
                                            "justify-between pr-1 pl-3 font-normal shadow-none overflow-hidden text-ellipsis hover:bg-white dark:hover:bg-slate-900",
                                            !field.value && "text-slate-500"
                                        )}
                                    >
                                        {field.value
                                            ? options?.find((o) => o.value === field.value)?.label
                                            : (placeholder ?? copy.select)}

                                        <div className="flex flex-row items-center">
                                            {disabled ? (
                                                <Button
                                                    tabIndex={-1}
                                                    variant="ghost"
                                                    size="icon"
                                                    type="button"
                                                    disabled
                                                >
                                                    <Lock size={16} />
                                                </Button>
                                            ) : (
                                                <Button tabIndex={-1} variant="ghost" size="icon" type="button">
                                                    <ChevronsUpDown size={16} />
                                                </Button>
                                            )}
                                        </div>
                                    </Button>
                                </FormControl>
                            </PopoverTrigger>
                            {clearable && field.value != null && field.value !== "" && (
                                <Button
                                    tabIndex={-1}
                                    variant="outline"
                                    size="icon"
                                    type="button"
                                    className="bg-white bg-opacity-100 ml-2 rounded-md w-12 h-12 text-slate-600"
                                    onClick={() => {
                                        field.onChange(null);
                                    }}
                                >
                                    <X size={16} />
                                    <span className="sr-only">{copy.clear}</span>
                                </Button>
                            )}
                        </div>
                        <PopoverContent className="relative p-0 rounded-lg min-w-[var(--radix-popover-trigger-width)] overflow-hidden">
                            <Command shouldFilter={false}>
                                <CommandInput
                                    data-testid="combobox-search-input"
                                    placeholder={copy.search}
                                    className="h-9"
                                    value={search}
                                    onValueChange={setSearch}
                                />
                                <CommandEmpty>{copy.empty}</CommandEmpty>
                                <div className={cn("max-h-[400px] overflow-y-auto", (addOption || footer) && "pb-12")}>
                                    <CommandGroup>
                                        {displayedOptions.map((o) => (
                                            <CommandItem
                                                data-testid={getOptionTestId(o)}
                                                role="option"
                                                aria-selected={o.value === field.value}
                                                value={String(o.value)}
                                                key={String(o.value)}
                                                showCheck={false}
                                                disabled={o.value == null || String(o.value).length === 0}
                                                className={cn(optionTrailing && "flex flex-row items-center gap-1")}
                                                onMouseDown={(e) => {
                                                    // Keep focus within cmdk so onSelect fires reliably inside Popover
                                                    e.preventDefault();
                                                }}
                                                onSelect={(selectedValue) => {
                                                    const selected = options?.find(
                                                        (opt) => String(opt.value) === selectedValue
                                                    );
                                                    if (!selected) return;
                                                    field.onChange(selected.value);
                                                    setOpen(false);
                                                }}
                                            >
                                                {optionTrailing ? (
                                                    <span
                                                        role="presentation"
                                                        className="shrink-0"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                    >
                                                        {optionTrailing(o)}
                                                    </span>
                                                ) : null}
                                                <span className="flex-1 min-w-0 truncate">{o.label}</span>
                                                <Check
                                                    size={16}
                                                    className={cn(
                                                        "ml-auto shrink-0",
                                                        o.value === field.value ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </div>
                            </Command>
                            {(addOption || footer) && (
                                <div className="right-0 bottom-0 left-0 absolute bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 border-t">
                                    {footer}
                                    {addOption ? (
                                        <Dialog open={addOptionOpen} onOpenChange={setAddOptionOpen}>
                                            <DialogTrigger
                                                className={cn(
                                                    buttonVariants({
                                                        size: "sm",
                                                        variant: "ghost",
                                                    }),
                                                    "flex items-center gap-2 w-full"
                                                )}
                                            >
                                                <Plus size={20} />
                                                {copy.addOption}
                                            </DialogTrigger>
                                            <DialogContent onSubmit={(event) => event.stopPropagation()}>
                                                <DialogHeader>
                                                    <DialogTitle>{copy.addOption}</DialogTitle>
                                                </DialogHeader>
                                                {typeof addOption === "function"
                                                    ? addOption(() => setAddOptionOpen(false))
                                                    : addOption}
                                            </DialogContent>
                                        </Dialog>
                                    ) : null}
                                </div>
                            )}
                        </PopoverContent>
                    </Popover>

                    <FormMessage />
                </FormItem>
            )}
        />
    );
}

export default ComboBox;
